import IncompleteAssessment from '@/components/assessment-incomplete/assessment-incomplete';
import DashboardLayout from '@/components/dashboard/dashboard-layout/dashboard-layout';
import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import { QuestionnaireStatus } from '@/components/decision-tree';
import useIsMobile from '@/hooks/use-is-mobile';
import useAnalyticsCalls from '@/hooks/use-analytics';
import DashboardHeaderIncomplete from './components/dashboard-header-incomplete/dashboard-header-incomplete';
import useDashboardController from './dashboard.controller';
import DashboardHeaderV2 from './components/dashboard-header-v2';
import { TabType } from '../health-data/components/health-data-tabs';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDashboardTabsConfig } from './components/dashboard-tabs-config/dashboard-tabs-config';

export default function DashboardPage() {
  const { name, questionnaireStatus, assessmentProgress } = useDashboardController();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { callDashboardTabChanged } = useAnalyticsCalls();

  const { tabsConfig } = useDashboardTabsConfig({ setParams });

  const tabsArray = useMemo(() => Object.values(tabsConfig), [tabsConfig]);
  const activeTabId = (params.get('tab') || 'risk-insights') as TabType;
  const activeTab = tabsConfig[activeTabId];

  // Memoize tab change handler
  const handleTabChange = useCallback(
    (tabId: TabType) => {
      // Track tab change
      callDashboardTabChanged(activeTabId, tabId);

      const newParams: Record<string, string> = { tab: tabId };
      setParams(newParams);
    },
    [setParams, activeTabId, callDashboardTabChanged],
  );

  const renderSwitch = (param: string) => {
    switch (param) {
      case QuestionnaireStatus.uncompleted:
        return (
          <>
            <DashboardHeaderIncomplete name={name} />
            <DashboardLayout contentProps={{ display: { base: 'block', lg: 'flex' } }}>
              <IncompleteAssessment assessmentProgress={assessmentProgress} variant={isMobile ? 'small' : 'large'} />
            </DashboardLayout>
          </>
        );
      case QuestionnaireStatus.inaccurate:
      case QuestionnaireStatus.completed:
        return (
          <>
            <DashboardHeaderV2
              heroContent={activeTab.heroContent}
              tabsConfig={{
                activeTab,
                onTabChange: handleTabChange,
                tabs: tabsArray,
              }}
            />
            {/* Tab Content */}
            <div role="tabpanel" id={`tabpanel-${activeTab.id}`} aria-labelledby={`tab-${activeTab.id}`} tabIndex={0}>
              {activeTab.tabContent}
            </div>
          </>
        );
    }
  };

  return (
    <>
      <DashboardNavigationBar activeItem="home" />
      <div
        style={{
          width: '100%',
          height: '100%',
          textAlign: 'center',
          paddingBottom: 'var(--spacer-5)',
          flexGrow: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
        className="md:pb-8 lg:pb-12"
      >
        {questionnaireStatus && renderSwitch(questionnaireStatus)}
      </div>
    </>
  );
}
